import { loadScript } from './dom';

export const init = () => {
  const fullMap = document.querySelector('[data-plugin="full-map"]');
  const locations = window.appLocations?.locations;

  if (fullMap && locations?.length && window.appLocations?.GOOGLE_MAPS_API_KEY) {
    loadGoogleMaps(() => {
      useFullMap({ el: fullMap, locations });
    });
  }
};

const ICONS = {
  nvision: '/wp-content/themes/nvision-v2/ui/icon/NvisionMarker.png',
  affiliate: '/wp-content/themes/nvision-v2/ui/icon/AffiliateMarker.svg',
};

const useFullMap = ({ el, locations }) => {
  const mapEl = el.querySelector('[data-target="map"]');
  const mapControls = [...el.querySelectorAll('[name="location_marker_control"]')];

  const map = new google.maps.Map(mapEl, {
    center: { lat: 40.7128, lng: -74.006 },
    zoom: 8,
    mapId: 'e6f2a4c7d4c196d1', // Ray's map
    // mapId: '5d5a2b5ba3280bc0', // Nvision's map
  });

  let lastInfoWindow = null;
  const closeLastWindow = () => {
    if (lastInfoWindow) {
      lastInfoWindow.close();
    }
  };

  // create markers with icon custom colors
  const markers = locations
    .filter(
      (location) =>
        location.address.latitude && location.address.longitude && location.address.line_1
    )
    .map((location) => {
      const marker = new google.maps.Marker({
        position: {
          lat: Number(location.address.latitude),
          lng: Number(location.address.longitude),
        },
        map,
        title: location.title,
        icon: location.affiliate ? ICONS.affiliate : ICONS.nvision,
      });

      // Get star rating html
      const starRatingHTML = renderStarRating(location.reviews);

      // add info window
      const infoWindow = new google.maps.InfoWindow({
        content: `<div class="flex flex-col p-2 md:p-4">
        <h3 class="Heading-4 mb-2 font-sans">
          <a href="${location.url}" class="hover:underline">${location.title}</a></h3>
        ${starRatingHTML}
        <p class="text-lg mb-6 font-sans">
          ${location.address.line_1}<br />${location.address.line_2}
        </p>
        <div class="flex flex-col md:flex-row mb-4">
          <a
            href="${location.url}"
            class="bg-blue px-6 py-3 text-white text-sm font-bold font-sans"
          >
            Learn More
          </a>
        </div>
        <div class="flex gap-8 text-lg">
          <a
            href="${location.directionsUrl}"
            class="text-blue text-sm underline hover:no-underline font-sans font-medium"
            target="_blank" rel="noopener noreferrer"
          >
            Directions
          </a>
          <a
            class="text-blue text-sm underline hover:no-underline font-sans font-medium"
            href="tel:+1-${location.phone}"
          >
            Call ${location.phone}
          </a>
        </div>
      </div>`,
      });

      marker.addListener('click', () => {
        closeLastWindow();
        infoWindow.open(map, marker);
        lastInfoWindow = infoWindow;
      });

      const hideMarker = () => {
        marker.setVisible(false);
      };
      const showMarker = () => {
        marker.setVisible(true);
      };

      return { location, marker, hideMarker, showMarker };
    });

  fitMapToMarkers(
    map,
    markers.map((m) => m.marker)
  );

  setupMapControls({ mapControls, map, markers, closeLastWindow });
};

const setupMapControls = ({ mapControls, map, markers, closeLastWindow }) => {
  if (!mapControls.length) return;

  mapControls.forEach((control) => {
    control.addEventListener('change', (e) => {
      const { value } = e.target;
      const shownMarkers = markers.map((marker) => {
        closeLastWindow();
        if (value === 'all') {
          marker.showMarker();
          return marker;
        }

        const isAffiliate = marker.location.affiliate;
        if (value === 'nvision' && !isAffiliate) {
          marker.showMarker();
          return marker;
        }

        if (value === 'affiliates' && isAffiliate) {
          marker.showMarker();
          return marker;
        }

        marker.hideMarker();
        return marker;
      });

      fitMapToMarkers(
        map,
        shownMarkers.map((m) => m.marker)
      );
    });
  });
};

const fitMapToMarkers = (map, markers) => {
  const bounds = new google.maps.LatLngBounds();
  markers.forEach((marker) => {
    bounds.extend(marker.getPosition());
  });
  map.fitBounds(bounds);

  setMinimumZoom(map);
};

const setMinimumZoom = (map, zoom = 14) => {
  const listener = google.maps.event.addListener(map, 'idle', () => {
    if (map.getZoom() > zoom) map.setZoom(zoom);
    google.maps.event.removeListener(listener);
  });
};

export const loadGoogleMaps = (callback) => {
  const mapsUrl = `https://maps.googleapis.com/maps/api/js?key=${window.appLocations.GOOGLE_MAPS_API_KEY}`;
  loadScript(mapsUrl, callback);
};

const renderStarRating = ({ rating, review_count }) => {
  if (!rating) {
    return '';
  }

  const emptyColor = 'text-gray/40';
  const fullStars = Math.floor(rating);
  const partialStar = rating - fullStars;
  const emptyStars = 5 - Math.ceil(rating);

  const starSize = 'w-6 h-6'; // 24
  const starColor = 'text-gold';

  let starsHTML = `<div class="relative flex gap-1">`;

  for (let i = 0; i < fullStars; i++) {
    starsHTML += renderStar(starSize, starColor);
  }

  if (partialStar > 0) {
    starsHTML += `<div class="relative">
      <div class="absolute t-0 left-0 flex overflow-hidden z-10" style="width: ${partialStar * 100
      }%;">${renderStar(starSize, starColor)}</div>
      ${renderStar(starSize, emptyColor)}
    </div>`;
  }

  for (let i = 0; i < emptyStars; i++) {
    starsHTML += renderStar(starSize, emptyColor);
  }

  starsHTML += `</div>`;

  return `<div class="flex items-center mb-2">
    ${starsHTML}
    <span class="ml-4 text-sm text-gray-700">${review_count} reviews</span>
  </div>`;
};

const renderStar = (size, color) => {
  return `<i class="relative flex-shrink-0 flex justify-center items-center ${size} ${color}">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="max-w-full max-h-full w-full h-full">
      <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
    </svg>
  </i>`;
};
